/**
 * Основной компонент Git-графа
 * Объединяет все компоненты визуализации в единую SVG-диаграмму
 */

import { useMemo, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { useGitStore } from '@/store/gitStore';
import { layoutGraph, calculateGraphSize } from './GraphLayout';
import { CommitNode } from './CommitNode';
import { CommitEdge, EdgeDefs } from './CommitEdge';
import { BranchPointers } from './BranchPointer';
import { HeadPointer } from './HeadPointer';
import { useGraphAnimation } from './useGraphAnimation';
import type { GraphConfig, NodePosition, HighlightState } from './types';
import { DEFAULT_GRAPH_CONFIG } from './types';

interface GitGraphProps {
  /** Конфигурация графа */
  config?: Partial<GraphConfig>;
  /** Класс для контейнера */
  className?: string;
  /** Показывать ли заголовок */
  showHeader?: boolean;
  /** Режим компактного отображения */
  compact?: boolean;
  /** Callback при клике на коммит */
  onCommitClick?: (sha: string) => void;
  /** Callback при клике на ветку */
  onBranchClick?: (branchName: string) => void;
  /** Подсвеченные элементы (для интеграции со сценариями) */
  highlights?: HighlightState[];
}

/**
 * Компонент визуализации Git-графа
 */
export function GitGraph({
  config,
  className,
  showHeader = true,
  compact = false,
  onCommitClick,
  onBranchClick,
  highlights = [],
}: GitGraphProps) {
  const { repo, getHeadCommit, getCurrentBranch } = useGitStore();

  // Получаем HEAD коммит и текущую ветку
  const headCommit = getHeadCommit();
  const currentBranch = getCurrentBranch();
  const headSha = headCommit?.sha ?? null;

  // Вычисляем состояние графа
  const graphState = useMemo(() => {
    if (!repo.initialized || !headSha) {
      return null;
    }

    return layoutGraph(repo.commits, repo.branches, headSha, currentBranch, config);
  }, [repo.commits, repo.branches, headSha, currentBranch, config, repo.initialized]);

  // Анимации
  const { nodeStates, isEdgeNew, isAnimating } = useGraphAnimation(
    graphState ?? {
      nodes: new Map(),
      edges: [],
      branches: new Map(),
      headSha: null,
      currentBranch: null,
    }
  );

  // Размеры графа
  const graphSize = useMemo(() => {
    if (!graphState) {
      return { width: DEFAULT_GRAPH_CONFIG.width, height: DEFAULT_GRAPH_CONFIG.height };
    }
    return calculateGraphSize(graphState, config);
  }, [graphState, config]);

  // Позиции HEAD для указателя
  const headPosition = useMemo((): NodePosition | null => {
    if (!graphState || !headSha) return null;
    const headNode = graphState.nodes.get(headSha);
    return headNode?.position ?? null;
  }, [graphState, headSha]);

  // Позиции голов веток
  const branchHeads = useMemo(() => {
    const heads = new Map<string, NodePosition>();
    if (!graphState) return heads;

    for (const [branchName, sha] of Object.entries(repo.branches)) {
      const node = graphState.nodes.get(sha);
      if (node) {
        heads.set(branchName, node.position);
      }
    }
    return heads;
  }, [graphState, repo.branches]);

  // Обработчик клика на коммит
  const handleCommitClick = useCallback(
    (sha: string) => {
      onCommitClick?.(sha);
    },
    [onCommitClick]
  );

  // Обработчик клика на ветку
  const handleBranchClick = useCallback(
    (branchName: string) => {
      onBranchClick?.(branchName);
    },
    [onBranchClick]
  );

  // Получение подсветки для коммита
  const getCommitHighlight = useCallback(
    (sha: string): HighlightState | undefined => {
      return highlights.find((h) => h.type === 'commit' && h.id === sha);
    },
    [highlights]
  );

  // Если репозиторий не инициализирован
  if (!repo.initialized) {
    return (
      <div
        className={cn('flex items-center justify-center h-full bg-slate-950 rounded-lg', className)}
      >
        <div className="text-center text-slate-500">
          <div className="text-4xl mb-2">📁</div>
          <p className="text-sm">Репозиторий не инициализирован</p>
          <p className="text-xs mt-1">Используйте git init для начала работы</p>
        </div>
      </div>
    );
  }

  // Если нет коммитов
  if (!graphState || graphState.nodes.size === 0) {
    return (
      <div
        className={cn('flex items-center justify-center h-full bg-slate-950 rounded-lg', className)}
      >
        <div className="text-center text-slate-500">
          <div className="text-4xl mb-2">📝</div>
          <p className="text-sm">Нет коммитов</p>
          <p className="text-xs mt-1">Создайте первый коммит с помощью git commit</p>
        </div>
      </div>
    );
  }

  const fullConfig: GraphConfig = { ...DEFAULT_GRAPH_CONFIG, ...config };

  return (
    <div className={cn('relative bg-slate-950 rounded-lg overflow-hidden', className)}>
      {/* Заголовок */}
      {showHeader && (
        <div className="absolute top-0 left-0 right-0 z-10 px-4 py-2 bg-slate-900/80 backdrop-blur-sm border-b border-slate-800">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-slate-300">
              Git Graph
              {currentBranch && (
                <span className="ml-2 px-2 py-0.5 rounded bg-git-orange/20 text-git-orange text-xs">
                  {currentBranch}
                </span>
              )}
            </h4>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>{graphState.nodes.size} коммитов</span>
              <span>•</span>
              <span>{graphState.branches.size} веток</span>
            </div>
          </div>
        </div>
      )}

      {/* SVG контейнер */}
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${graphSize.width} ${graphSize.height}`}
        className={cn('transition-opacity duration-300', showHeader && 'pt-10')}
        style={{ minHeight: compact ? 200 : 300 }}
      >
        {/* Определения (градиенты, фильтры) */}
        <EdgeDefs />

        {/* Фоновая сетка (опционально) */}
        <defs>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path
              d="M 20 0 L 0 0 0 20"
              fill="none"
              stroke="rgba(255,255,255,0.03)"
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />

        {/* Рёбра (линии связей) */}
        <g className="edges">
          {graphState.edges.map((edge) => (
            <CommitEdge
              key={`${edge.fromSha}-${edge.toSha}`}
              edge={edge}
              config={fullConfig}
              isNew={isEdgeNew(edge.fromSha, edge.toSha)}
            />
          ))}
        </g>

        {/* Указатели веток */}
        <BranchPointers
          branches={graphState.branches}
          branchHeads={branchHeads}
          currentBranch={currentBranch}
          config={fullConfig}
          onBranchClick={handleBranchClick}
        />

        {/* Узлы коммитов */}
        <g className="nodes">
          {Array.from(graphState.nodes.values()).map((node) => {
            const animState = nodeStates.get(node.commit.sha);
            const highlight = getCommitHighlight(node.commit.sha);

            return (
              <CommitNode
                key={node.commit.sha}
                node={node}
                config={fullConfig}
                isNew={animState?.isNew ?? false}
                isAnimating={animState?.isAnimating ?? false}
                highlight={highlight}
                onClick={handleCommitClick}
              />
            );
          })}
        </g>

        {/* HEAD указатель */}
        {headPosition && (
          <HeadPointer
            position={headPosition}
            isDetached={repo.isDetached}
            currentBranch={currentBranch}
            config={fullConfig}
            isAnimating={isAnimating}
          />
        )}
      </svg>

      {/* Легенда */}
      {!compact && (
        <div className="absolute bottom-0 left-0 right-0 px-4 py-2 bg-slate-900/80 backdrop-blur-sm border-t border-slate-800">
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-git-orange" />
              <span className="text-slate-500">main</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-blue-400" />
              <span className="text-slate-500">feature</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-purple-400" />
              <span className="text-slate-500">develop</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-yellow-400" />
              <span className="text-slate-500">hotfix</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-green-400" />
              <span className="text-slate-500">release</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GitGraph;
